'use client';
import { useState, useRef, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Leaf, ChevronRight, LogOut, LayoutDashboard } from 'lucide-react';
import PageLoader from '@/components/PageLoader';
import { useAuth } from '@/contexts/AuthContext';

const navLinks = [
  { href: '/home-page', label: 'হোম' },
  { href: '/crop-analysis-page', label: 'ফসল বিশ্লেষণ' },
  { href: '/ai-chat-page', label: 'AI চ্যাট' },
  { href: '/farmer-dashboard', label: 'ড্যাশবোর্ড' },
  { href: '/about-page', label: 'আমাদের সম্পর্কে' },
];

// Global event to open ChashiChatbot from anywhere
export function openChashiChatbot() {
  window.dispatchEvent(new CustomEvent('chashi-open'));
}

export default function PublicNav() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, profile, signOut, loading } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [navMessage, setNavMessage] = useState('লোড হচ্ছে...');
  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);

  // Close avatar dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setAvatarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleNavClick = (href: string, label: string) => {
    if (pathname === href) return;
    setNavMessage(`${label} লোড হচ্ছে...`);
    setNavigating(true);
    setMobileOpen(false);
    setTimeout(() => router.push(href), 80);
  };

  const handleSignOut = async () => {
    setAvatarOpen(false);
    setNavMessage('সাইন আউট হচ্ছে...');
    setNavigating(true);
    try {
      await signOut();
    } catch {}
    setTimeout(() => router.push('/login'), 80);
  };

  const handleOpenChashi = () => {
    setMobileOpen(false);
    window.dispatchEvent(new CustomEvent('chashi-open'));
  };

  // Derive display name and avatar
  const displayName = profile?.full_name || user?.user_metadata?.full_name || user?.email || 'User';
  const avatarUrl = profile?.avatar_url || user?.user_metadata?.avatar_url || null;
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const isLoggedIn = !loading && !!user;

  return (
    <>
      {navigating && <PageLoader message={navMessage} />}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-green-100 shadow-sm">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo */}
            <button
              onClick={() => handleNavClick('/home-page', 'Home')}
              className="flex items-center gap-2 sm:gap-2.5 transition-all duration-200 hover:scale-105 hover:opacity-90 active:scale-100"
            >
              <AppLogo size={32} />
              <span className="font-bold text-base sm:text-lg text-green-800 tracking-tight">AgroGuard AI</span>
            </button>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks?.map((link, i) => (
                <button
                  key={link?.href}
                  onClick={() => handleNavClick(link.href, link.label)}
                  style={{ animationDelay: `${i * 50}ms` }}
                  className={
                    pathname === link?.href
                      ? 'nav-link-active' : 'nav-link px-3 py-1.5 rounded-lg hover:bg-green-50 hover:-translate-y-0.5 active:translate-y-0 active:scale-95'
                  }
                >
                  {link?.label}
                </button>
              ))}
            </div>

            {/* Desktop CTAs */}
            <div className="hidden md:flex items-center gap-2">
              {/* চাষী AI button */}
              <button
                onClick={handleOpenChashi}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 text-white text-sm font-semibold shadow-sm hover:shadow-md hover:scale-105 active:scale-95 transition-all duration-200 border border-green-400/30"
                aria-label="চাষী AI চ্যাটবট খুলুন"
              >
                <span className="text-base leading-none">🌾</span>
                <span>চাষী</span>
                <span className="bg-yellow-400 text-green-900 text-[9px] font-bold px-1 py-0.5 rounded-full leading-none">AI</span>
              </button>

              {isLoggedIn ? (
                /* Avatar + dropdown */
                <div className="relative" ref={avatarRef}>
                  <button
                    onClick={() => setAvatarOpen((v) => !v)}
                    className="flex items-center gap-2 p-1 rounded-full hover:ring-2 hover:ring-green-400 transition-all duration-200 focus:outline-none"
                    aria-label="User menu"
                  >
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={displayName}
                        className="w-9 h-9 rounded-full object-cover border-2 border-green-200"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white text-sm font-bold border-2 border-green-200">
                        {initials}
                      </div>
                    )}
                  </button>

                  {/* Dropdown */}
                  {avatarOpen && (
                    <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-green-100 py-1 z-50 animate-fade-in">
                      <div className="px-4 py-2.5 border-b border-green-50">
                        <p className="text-sm font-semibold text-gray-800 truncate">{displayName}</p>
                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                      </div>
                      <button
                        onClick={() => { setAvatarOpen(false); handleNavClick('/farmer-dashboard', 'ড্যাশবোর্ড'); }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors"
                      >
                        <LayoutDashboard size={15} />
                        ড্যাশবোর্ড
                      </button>
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut size={15} />
                        সাইন আউট
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => handleNavClick('/login', 'সাইন ইন')}
                  className="btn-secondary text-sm py-2 px-4 btn-ripple"
                >
                  সাইন ইন
                </button>
              )}
              <button
                onClick={() => handleNavClick('/crop-analysis-page', 'ফসল বিশ্লেষণ')}
                className="btn-primary text-sm py-2 px-4 flex items-center gap-1.5 btn-ripple group"
              >
                <Leaf size={15} className="transition-transform duration-300 group-hover:rotate-12" />
                ফসল বিশ্লেষণ করুন
              </button>
            </div>

            {/* Mobile right side: avatar + hamburger */}
            <div className="md:hidden flex items-center gap-2">
              {/* Mobile avatar when logged in */}
              {isLoggedIn && (
                <button
                  onClick={() => handleNavClick('/farmer-dashboard', 'ড্যাশবোর্ড')}
                  className="flex items-center justify-center active:scale-90 transition-transform duration-150"
                >
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={displayName} className="w-8 h-8 rounded-full object-cover border-2 border-green-200" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white text-xs font-bold border-2 border-green-200">
                      {initials}
                    </div>
                  )}
                </button>
              )}
              {/* Hamburger */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-green-50 transition-all duration-200 active:scale-90"
                aria-label="Toggle menu"
              >
                <span className="block transition-all duration-300">
                  {mobileOpen ? <X size={22} className="text-gray-700" /> : <Menu size={22} className="text-gray-700" />}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile full-screen drawer overlay */}
        {mobileOpen && (
          <div className="md:hidden fixed inset-0 top-14 z-40 bg-black/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
        )}

        {/* Mobile drawer */}
        <div className={`md:hidden fixed top-14 left-0 right-0 z-50 bg-white shadow-2xl border-t border-green-100 transition-all duration-300 ease-out ${mobileOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0 pointer-events-none'}`}>
          <div className="px-4 py-4 space-y-1.5 max-h-[calc(100vh-3.5rem)] overflow-y-auto">
            {/* Mobile user info when logged in */}
            {isLoggedIn && (
              <div className="flex items-center gap-3 px-3 py-3 mb-2 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-100">
                {avatarUrl ? (
                  <img src={avatarUrl} alt={displayName} className="w-10 h-10 rounded-full object-cover border-2 border-green-200" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white text-sm font-bold border-2 border-green-200">
                    {initials}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-800 truncate">{displayName}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
              </div>
            )}

            {navLinks?.map((link) => (
              <button
                key={link?.href}
                onClick={() => handleNavClick(link.href, link.label)}
                className={`w-full flex items-center justify-between px-4 py-3.5 rounded-2xl text-sm font-semibold transition-all duration-200 active:scale-95 ${
                  pathname === link?.href
                    ? 'bg-green-600 text-white shadow-sm' : 'text-gray-700 hover:bg-green-50 hover:text-green-700'
                }`}
              >
                {link?.label}
                <ChevronRight size={16} className={`transition-transform duration-200 ${pathname === link?.href ? 'opacity-80' : 'opacity-40'}`} />
              </button>
            ))}

            {/* চাষী AI button in mobile menu */}
            <button
              onClick={handleOpenChashi}
              className="w-full flex items-center justify-between px-4 py-3.5 rounded-2xl text-sm font-semibold bg-gradient-to-r from-green-500 to-emerald-600 text-white transition-all duration-200 active:scale-95 shadow-sm"
            >
              <span className="flex items-center gap-2">
                <span className="text-base">🌾</span>
                চাষী AI — কৃষি সহায়তা
              </span>
              <span className="bg-yellow-400 text-green-900 text-[9px] font-bold px-1.5 py-0.5 rounded-full">AI</span>
            </button>

            <div className="pt-2 pb-2 grid grid-cols-2 gap-2">
              {isLoggedIn ? (
                <button
                  onClick={handleSignOut}
                  className="flex items-center justify-center gap-1.5 py-3 rounded-2xl text-sm font-semibold text-red-600 border border-red-200 hover:bg-red-50 transition-all duration-200 active:scale-95"
                >
                  <LogOut size={14} />
                  Sign Out
                </button>
              ) : (
                <button
                  onClick={() => handleNavClick('/login', 'Sign In')}
                  className="btn-secondary text-sm py-3 text-center btn-ripple rounded-2xl"
                >
                  Sign In
                </button>
              )}
              <button
                onClick={() => handleNavClick('/crop-analysis-page', 'Crop Analysis')}
                className="btn-primary text-sm py-3 text-center btn-ripple rounded-2xl flex items-center justify-center gap-1.5"
              >
                <Leaf size={14} />
                Analyse Crop
              </button>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}